import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  Field,
  HTMLWrapper,
  Input,
  Flex,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import type { ChangeEvent, KeyboardEvent } from "react";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface NumberQuestionProps {
  question: Question;
}

export const validate = async (
  value: string,
  question: Question
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.max(question.maxLength || 255).validate(value);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const NumberQuestion = ({ question }: NumberQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const maxLength = question.maxLength || 255;
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value.length > maxLength || parseInt(e.target.value) < 0)
      return;

    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  const blockInvalidChar = (e: KeyboardEvent<HTMLInputElement>) =>
    ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

  return (
    <Field.Root
      my={4}
      id={`${question.id}`}
      invalid={error.length > 0}
      required={question.required}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <Input
        type="number"
        value={answer}
        maxLength={maxLength}
        onChange={handleChange}
        inputMode="numeric"
        pattern="[0-9]*"
        onKeyDown={blockInvalidChar}
        size={{ base: "lg", lg: "md" }}
        data-testid="numberQuestion-input"
        name="numberQuestion-input"
        autoComplete="off"
      />
      <Flex justify="space-between">
        <div>
          {error.length > 0 && (
            <Field.ErrorText data-testid="numberQuestion-errorMessage">
              <Field.ErrorIcon />
              {translate(`errors.${error}`)}
            </Field.ErrorText>
          )}
          <Field.HelperText>{question.notes}</Field.HelperText>
        </div>
        <Field.HelperText>{`${`${answer}`.length}/${maxLength}`}</Field.HelperText>
      </Flex>
    </Field.Root>
  );
};

export default NumberQuestion;
